/**
 * Die Rechnung hinter **Geschafft** (CONTEXT.md) — rein, ohne DB, ohne React.
 *
 * Sie nimmt **Einträge**, nicht Aufgaben: Ein Eintrag ist alles, was eine
 * Kennung und einen Zeitpunkt hat. Heute sind das erledigte Aufgaben; kommen
 * die **Gewohnheiten** dazu, laufen sie durch dieselbe Rechnung, ohne dass sie
 * angefasst wird.
 *
 * Alles rechnet in der LOKALEN Zeit: Ein Tag endet um Mitternacht beim
 * Nutzer, nicht in UTC — dieselbe Regel wie bei den Gewohnheiten
 * (`features/habits/lib/date.ts`; nicht importiert, weil Features sich nicht
 * gegenseitig importieren).
 */

export interface DoneEntry {
  id: string
  /** Wann es geschafft wurde. */
  at: Date
}

export interface DoneDay<E extends DoneEntry = DoneEntry> {
  /** `'YYYY-MM-DD'`, lokal. */
  key: string
  /** Lokale Mitternacht des Tages. */
  date: Date
  /** Neueste zuerst. */
  entries: E[]
}

/**
 * Was über einem Tag steht: „Heute", „Gestern", fünf Tage lang der Wochentag
 * allein, ab sieben Tagen zurück das Datum. Der Wochentag trägt genau so weit,
 * wie die Erinnerung reicht, welcher Tag was war.
 */
export type DayLabel =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'weekday'; date: Date }
  | { kind: 'date'; date: Date }

const MS_PER_DAY = 86_400_000
/** Sicherung gegen einen Datenbestand, der weiter zurückreicht als sinnvoll. */
const MAX_STREAK_LOOKBACK_DAYS = 2200

/** `'YYYY-MM-DD'` des lokalen Tages. */
export function dayKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addDays(d: Date, n: number): Date {
  const r = startOfDay(d)
  r.setDate(r.getDate() + n)
  return r
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY)
}

/** Lokale Mitternacht des Montags, der die Woche von `d` beginnt. */
function startOfWeek(d: Date): Date {
  const mondayFirst = (d.getDay() + 6) % 7
  return addDays(d, -mondayFirst)
}

/** Nach Tag gruppiert, neueste Tage zuerst, in jedem Tag das Neueste oben. */
export function groupByDay<E extends DoneEntry>(entries: E[], _now: Date): DoneDay<E>[] {
  const byKey = new Map<string, DoneDay<E>>()
  for (const e of entries) {
    const key = dayKey(e.at)
    let day = byKey.get(key)
    if (!day) {
      day = { key, date: startOfDay(e.at), entries: [] }
      byKey.set(key, day)
    }
    day.entries.push(e)
  }
  const days = [...byKey.values()]
  days.sort((a, b) => b.date.getTime() - a.date.getTime())
  for (const day of days) day.entries.sort((a, b) => b.at.getTime() - a.at.getTime())
  return days
}

export function dayLabel(date: Date, now: Date): DayLabel {
  const back = daysBetween(date, now)
  if (back <= 0) return { kind: 'today' }
  if (back === 1) return { kind: 'yesterday' }
  if (back < 7) return { kind: 'weekday', date: startOfDay(date) }
  return { kind: 'date', date: startOfDay(date) }
}

export function countToday(entries: DoneEntry[], now: Date): number {
  const today = dayKey(now)
  let n = 0
  for (const e of entries) if (dayKey(e.at) === today) n++
  return n
}

/** Die laufende Kalenderwoche **ab Montag** — nicht die letzten sieben Tage. */
export function countThisWeek(entries: DoneEntry[], now: Date): number {
  const from = startOfWeek(now).getTime()
  const until = addDays(now, 1).getTime()
  let n = 0
  for (const e of entries) {
    const t = e.at.getTime()
    if (t >= from && t < until) n++
  }
  return n
}

/**
 * Die **Serie**: aufeinanderfolgende Tage mit mindestens einem Eintrag,
 * rückwärts gezählt. Ist heute noch nichts geschafft, beginnt sie bei gestern
 * — sonst stünde sie jeden Morgen auf null, obwohl nichts gerissen ist.
 * Kein Gnadentag: Ein Tag ohne ist ein Tag ohne.
 *
 * @param days die `'YYYY-MM-DD'`-Schlüssel aller Tage mit Einträgen — ALLER,
 *   nicht nur der geladenen. Eine Serie von 45 Tagen aus einem 30-Tage-Fenster
 *   ergäbe 30, und die Kachel löge leise.
 */
export function streakDays(days: Set<string>, now: Date): number {
  let cursor = startOfDay(now)
  if (!days.has(dayKey(cursor))) cursor = addDays(cursor, -1)
  let n = 0
  while (n < MAX_STREAK_LOOKBACK_DAYS && days.has(dayKey(cursor))) {
    n++
    cursor = addDays(cursor, -1)
  }
  return n
}
